import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'

import { readEngagedSeconds } from './engaged-time'
import { EventType } from './events'
import type { GuideEligibilityReason } from './guide-eligibility'
import { getSessionContext } from './session-id'
import type { TrackingProperties } from './tracking-props'
import { uuidv7 } from './uuid'

type EligibilityState =
  | GuideEligibilityReason
  | 'not_started'
  | 'pending'
  | 'network_error'
  | 'invalid_response'
  | 'aborted'

interface GuideState {
  guideEligibility: EligibilityState
  guideEligibilityMs?: number
  guideSectionMounted: boolean
  guideChatState: 'not_requested' | 'loading' | 'mounted' | 'disabled'
  guideChatViewed: boolean
}

// One instance per route visit. Operational state never rerenders the page.
export class HubVisit {
  private visitId: string | null = null
  private startedAt: number | null = null
  private ended = false
  private revision = 0
  private identity: TrackingProperties = {}
  private layout: TrackingProperties = {}
  private interactions = 0
  private maxScroll = 0
  private sections = new Set<string>()
  private milestones = new Set<number>()
  private guide: GuideState = {
    guideEligibility: 'not_started',
    guideSectionMounted: false,
    guideChatState: 'not_requested',
    guideChatViewed: false,
  }

  constructor(readonly sourcePath: string) {}

  // Read only in effects or event callbacks: constructing the visit also runs
  // during prerender, where UUID timestamps and randomness must not be read.
  get id(): string {
    return (this.visitId ??= uuidv7())
  }

  setLayoutTracking = (layout: TrackingProperties) => {
    this.layout = layout
  }

  start = () => {
    if (this.startedAt !== null || this.ended) {
      return
    }
    this.startedAt = Date.now()
    const { sessionId, windowId } = getSessionContext()
    this.identity = {
      $session_id: sessionId,
      $window_id: windowId,
      $host: window.location.host,
      $current_url: window.location.origin + this.sourcePath,
      $pathname: this.sourcePath,
    }
    this.emit('hub_visit_started')
    if (this.layout.experimentId) {
      this.emit('hub_experiment_exposed')
    }
  }

  private emit = (event: string, properties: TrackingProperties = {}) => {
    trackCustomEvent(event, EventType.Performance, {
      ...this.identity,
      ...this.layout,
      surface: 'hearing-aids-hub',
      hubVisitId: this.id,
      sourcePath: this.sourcePath,
      designVersion: 'hub-2026-10-15',
      trackingVersion: 2,
      ...properties,
    })
  }

  track = (event: string, properties: TrackingProperties = {}) => {
    if (!this.ended) {
      this.start()
      this.emit(event, properties)
    }
  }

  updateGuide = (state: Partial<GuideState>) => {
    Object.assign(this.guide, state)
  }

  sectionMounted = () => {
    if (!this.guide.guideSectionMounted) {
      this.guide.guideSectionMounted = true
      this.track('hub_guide_section_mounted')
    }
  }

  chatLoading = () => {
    if (this.guide.guideChatState === 'not_requested') {
      this.guide.guideChatState = 'loading'
      this.track('hub_guide_chat_loading')
    }
  }

  chatMounted = (enabled: boolean) => {
    const state = enabled ? 'mounted' : 'disabled'
    if (this.guide.guideChatState !== state) {
      this.guide.guideChatState = state
      this.track('hub_guide_chat_mounted', { enabled })
    }
  }

  chatViewed = () => {
    this.guide.guideChatViewed = true
  }

  sectionViewed = (section: string) => this.sections.add(section)
  interacted = () => this.interactions++
  scrolled = (fraction: number) => {
    this.maxScroll = Math.max(this.maxScroll, Math.min(1, Math.max(0, fraction)))
  }

  checkEngagement = () => {
    if (this.startedAt === null || this.ended) {
      return
    }
    const engagedSeconds = readEngagedSeconds()
    for (const thresholdSeconds of [10, 30, 60]) {
      if (engagedSeconds >= thresholdSeconds && !this.milestones.has(thresholdSeconds)) {
        this.milestones.add(thresholdSeconds)
        this.track('hub_engaged', { thresholdSeconds, engagedSeconds })
      }
    }
  }

  snapshot = (reason: 'hidden' | 'pagehide' | 'route_change') => {
    if (this.startedAt === null || this.ended) {
      return
    }
    this.checkEngagement()
    this.track('hub_visit_snapshot', {
      ...this.guide,
      reason,
      revision: ++this.revision,
      engagedSeconds: readEngagedSeconds(),
      wallSeconds: Math.max(0, (Date.now() - this.startedAt) / 1_000),
      interactionCount: this.interactions,
      maxScroll: this.maxScroll,
      sectionsSeen: [...this.sections].join(','),
      visibility: document.visibilityState,
    })
  }

  end = () => {
    this.snapshot('route_change')
    // Strict Mode's initial effect rehearsal ends before the queued start.
    if (this.startedAt !== null) {
      this.ended = true
    }
  }
}
